import type { LogEntry, LogLevel, LogQuery } from "@skaile/workspaces/types";
import { Command } from "commander";
import { logErr } from "../../helpers.ts";
import { formatEntries, formatSingleEntry } from "./format.ts";
import { localQuery } from "./local-query.ts";
import { localTail } from "./local-tail.ts";
import { parseTime } from "./parse-time.ts";
import { remoteQuery } from "./remote-query.ts";
import { remoteTail } from "./remote-tail.ts";
import { resolveMode } from "./resolve-mode.ts";

const VALID_LEVELS = new Set<LogLevel>(["debug", "info", "warn", "error"]);

interface CliOpts {
  kind?: string;
  subkind?: string;
  instance?: string;
  level?: string[];
  since?: string;
  until?: string;
  search?: string;
  limit?: string;
  json?: boolean;
  local?: boolean;
  remote?: boolean;
  platformUrl?: string;
  projectDir?: string;
  projectId?: string;
  cursor?: string;
  tail?: boolean;
}

/**
 * Build the `skaile session logs <sessionId>` subcommand.
 *
 * Two resolution modes:
 *   - Local (auto when `<workspace>/.skaile/logs.db` is found): open SQLite RO
 *     and run a filtered SELECT. Zero round-trip.
 *   - Remote (auto when no local DB but a platform URL is configured): call
 *     `workspace.queryLogs` over tRPC. Requires `--project-id`.
 *
 * Override auto-detect with `--local` / `--remote`.
 *
 * Pass `--tail` to follow new entries after the history snapshot. Local mode
 * polls the SQLite file every 500ms; remote mode subscribes to the
 * `workspace.onLogEntry` SSE stream. Ctrl-C exits cleanly with code 0.
 */
export function makeSessionLogsCommand(): Command {
  const cmd = new Command("logs")
    .description("Query the per-session structured debug log store")
    .argument("<sessionId>", "Session ID to query")
    .option("--kind <kind>", "Filter by source kind (mount, connector, mcp, runner, ...)")
    .option("--subkind <subkind>", "Filter by source subkind")
    .option("--instance <instance>", "Filter by source instance")
    .option(
      "--level <level>",
      "Filter by level (debug|info|warn|error). Repeatable.",
      collectLevel,
      [] as string[],
    )
    .option("--since <iso|relative>", "Lower-bound timestamp (ISO 8601 or 30s/15m/2h/1d)")
    .option("--until <iso>", "Upper-bound timestamp")
    .option("--search <text>", "Substring match on message (LIKE %text%)")
    .option("--limit <n>", "Max entries to return (default 200)")
    .option("--cursor <id>", "Pagination cursor (returns entries with id < cursor)")
    .option("--json", "Emit raw JSON instead of pretty TTY output")
    .option("--local", "Force local SQLite read (skip remote)")
    .option("--remote", "Force remote tRPC call (skip local)")
    .option("--platform-url <url>", "Platform base URL (remote mode)")
    .option("--project-id <id>", "Project ID (required for remote mode)")
    .option("--project-dir <dir>", "Workspace search root (default: cwd)")
    .option("--tail", "Stream new entries as they are written (Ctrl-C to stop)")
    .action(async (sessionId: string, opts: CliOpts) => {
      try {
        await runSessionLogs(sessionId, opts);
      } catch (err) {
        logErr(err instanceof Error ? err.message : String(err));
        process.exitCode = 1;
      }
    });

  return cmd;
}

async function runSessionLogs(sessionId: string, opts: CliOpts): Promise<void> {
  const query = buildLogQuery(opts);

  const resolved = resolveMode({
    forceLocal: opts.local,
    forceRemote: opts.remote,
    projectDir: opts.projectDir,
    platformUrl: opts.platformUrl,
  });

  const { entries, nextCursor } = await fetchEntries(sessionId, opts, resolved, query);

  const out = formatEntries(entries, {
    json: opts.json,
    // In tail mode the cursor hint is misleading (we are about to follow live
    // entries, not page back), so suppress it.
    ...(nextCursor && !opts.tail ? { nextCursor } : {}),
  });
  if (out.length > 0) console.log(out);

  if (opts.tail) {
    await runTail({
      sessionId,
      opts,
      resolved,
      query,
      historyEntries: entries,
    });
  }
}

/** Translate parsed CLI options into a session-scoped {@link LogQuery} (sans `sessionId`). */
function buildLogQuery(opts: CliOpts): Omit<LogQuery, "sessionId"> {
  const levels = (opts.level ?? []).map(validateLevel);
  return {
    ...(opts.kind || opts.subkind || opts.instance
      ? {
          source: {
            ...(opts.kind ? { kind: opts.kind as NonNullable<LogQuery["source"]>["kind"] } : {}),
            ...(opts.subkind ? { subkind: opts.subkind } : {}),
            ...(opts.instance ? { instance: opts.instance } : {}),
          },
        }
      : {}),
    ...(levels.length > 0 ? { levels } : {}),
    ...(opts.since ? { since: parseTime(opts.since) } : {}),
    ...(opts.until ? { until: parseTime(opts.until) } : {}),
    ...(opts.search ? { search: opts.search } : {}),
    ...(opts.limit ? { limit: parsePositiveInt(opts.limit, "--limit") } : { limit: 200 }),
    ...(opts.cursor ? { cursor: opts.cursor } : {}),
  };
}

/** Fetch the history snapshot from whichever store the mode resolved to. */
async function fetchEntries(
  sessionId: string,
  opts: CliOpts,
  resolved: ReturnType<typeof resolveMode>,
  query: Omit<LogQuery, "sessionId">,
): Promise<{ entries: LogEntry[]; nextCursor: string | undefined }> {
  if (resolved.mode === "local") {
    const result = localQuery(resolved.dbPath, { ...query, sessionId });
    return { entries: result.entries, nextCursor: result.nextCursor };
  }
  if (!opts.projectId) {
    throw new Error(
      "Remote mode requires --project-id (the platform tRPC procedure scopes by project).",
    );
  }
  const result = await remoteQuery({
    url: resolved.url,
    ...(resolved.token ? { token: resolved.token } : {}),
    projectId: opts.projectId,
    sessionId,
    query,
  });
  return { entries: result.entries, nextCursor: result.nextCursor };
}

interface RunTailArgs {
  sessionId: string;
  opts: CliOpts;
  resolved: ReturnType<typeof resolveMode>;
  query: Omit<LogQuery, "sessionId">;
  historyEntries: LogEntry[];
}

async function runTail(args: RunTailArgs): Promise<void> {
  const controller = new AbortController();
  const onSigint = () => {
    controller.abort();
    // Ctrl-C is normal termination of a tail loop, not an error.
    process.exitCode = 0;
  };
  process.on("SIGINT", onSigint);

  const emit = (entry: LogEntry) => {
    process.stdout.write(`${formatSingleEntry(entry, { json: args.opts.json })}\n`);
  };

  try {
    if (args.resolved.mode === "local") {
      // Skip rows already shown by the history snapshot.
      const lastHistoryId = args.historyEntries.at(-1)?.id;
      await localTail({
        dbPath: args.resolved.dbPath,
        query: { ...args.query, sessionId: args.sessionId },
        ...(lastHistoryId ? { startAfterId: lastHistoryId } : {}),
        onEntry: emit,
        signal: controller.signal,
      });
    } else {
      if (!args.opts.projectId) {
        throw new Error("Remote tail requires --project-id");
      }
      await remoteTail({
        url: args.resolved.url,
        ...(args.resolved.token ? { token: args.resolved.token } : {}),
        projectId: args.opts.projectId,
        sessionId: args.sessionId,
        query: args.query,
        signal: controller.signal,
        onEntry: emit,
      });
    }
  } finally {
    process.removeListener("SIGINT", onSigint);
  }
}

function collectLevel(value: string, prev: string[]): string[] {
  return [...prev, value];
}

function validateLevel(level: string): LogLevel {
  if (!VALID_LEVELS.has(level as LogLevel)) {
    throw new Error(`Invalid --level: ${level}. Expected one of debug|info|warn|error.`);
  }
  return level as LogLevel;
}

function parsePositiveInt(value: string, flag: string): number {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error(`${flag} must be a positive integer (got ${value})`);
  }
  return n;
}
